<script setup lang="ts">
import { HeadingFieldsFragment, ParagraphRoomOverview } from '~/api/graphql/generated'
import { isMediaImage, isParagraphHeading, isParagraphIconSectionItem, isParagraphRoomOverviewItem } from '~/lib/utils'

// Standard section component pattern - receives entity prop
interface RoomOverviewProps {
  entity: ParagraphRoomOverview // Will be ParagraphRoomOverview once types are generated
  parentName?: string
}

const props = defineProps<RoomOverviewProps>()

const parentName = props.parentName || getCurrentInstance()?.parent?.type.__name || ''

// Transform GraphQL data to component props
const heading = computed<HeadingFieldsFragment | undefined>(() => {
  const entity = props.entity?.fieldHeading?.entity
  if (!isParagraphHeading(entity)) return undefined
  return {
    __typename: 'ParagraphHeading',
    fieldHeadingTitle: entity.fieldHeadingTitle ?? null,
    fieldHeadingType: entity.fieldHeadingType ?? null,
    fieldVisible: entity.fieldVisible ?? null,
  } satisfies HeadingFieldsFragment
})

const rooms = computed(() => {
  const items = props.entity?.fieldItems ?? []

  return items
    .map((wrapper) => wrapper?.entity)
    .filter(isParagraphRoomOverviewItem)
    .map((entity) => {
      const link = entity.fieldLink
      const linkOptions = (link?.options as { attributes?: { target?: string } } | null | undefined)?.attributes

      const icons =
        entity.fieldIcons
          ?.map((iconField) => iconField?.entity)
          .filter(isParagraphIconSectionItem)
          .map((iconEntity) => {
            const imageEntity = iconEntity.fieldImage?.entity
            return {
              icon: isMediaImage(imageEntity) ? imageEntity : undefined,
              variant: iconEntity.fieldLabel ?? '',
              value: iconEntity.fieldValue?.value ?? '',
            }
          }) ?? []

      const mainImageEntity = entity.fieldImage?.entity
      const floorPlanImageEntity = entity.fieldFloorPlanImage?.entity

      type Cta = {
        label: string
        url: string
        target: '_self' | '_blank'
      }

      const target: Cta['target'] = linkOptions?.target === '_blank' ? '_blank' : '_self'

      const cta: Cta | null = link
        ? {
            label: link.title ?? '',
            url: link.url?.path ?? '',
            target,
          }
        : null

      return {
        title: entity.fieldTitleFormatted?.value ?? '',
        description: entity.fieldDescription?.value ?? '',
        mainImage: isMediaImage(mainImageEntity) ? mainImageEntity : null,
        floorPlanImage: isMediaImage(floorPlanImageEntity) ? floorPlanImageEntity : null,
        icons,
        cta,
      }
    })
})
</script>

<template>
  <SectionSpecial :title="heading ?? ''" :parent-name="parentName" class="section-room-overview">
    <!-- Hidden heading for SEO when headingVisible is false -->
    <!-- <component v-if="heading && !heading.visible" :is="heading.headingLevel" class="sr-only" v-text="heading.title" /> -->

    <div v-if="rooms.length" class="room-overview-content">
      <div v-for="(room, index) in rooms" :key="`room-${index}`" class="room-item">
        <RoomOverviewItem
          :title="room.title"
          :description="room.description"
          :main-image="room.mainImage"
          :floor-plan-image="room.floorPlanImage"
          :icons="room.icons"
          :cta="room.cta"
        />
      </div>
    </div>
  </SectionSpecial>
</template>

<style scoped lang="scss">
.room-overview-content {
  @include breakpoint(t) {
    // Current width is 10 columns + 9 gaps
    // To get 11 columns + 10 gaps:
    // width = current width + (current width - 9 gaps) / 10 + 1 gap
    // Simplified: width = current width + (current width - 9 * var(--gap)) / 10 + var(--gap)
    width: calc(100% + (100% - 9 * var(--gap)) / 10 + var(--gap));
  }
}

.room-item {
  &:not(:last-child) {
    @include fluid(margin-bottom, 36px, 24px);
  }
}
</style>
